import { HANDOFF_LANG_KEY } from '../hooks/usePrefilledAddress';
import { isPortalI18nActive, portalLanguage } from '../i18n';

/**
 * The marketing site's own chrome, reproduced for the booking portal.
 *
 * Everything here is a transcription of what the site ships, so that a visitor
 * handed over from it does not meet a different header. The sources are:
 *
 * - links + labels: `aperio/site/build/shell.js` (the SSR `nav()` and `foot()`)
 * - phone / e-mail:  `aperio/site/build/util.js` (PHONE, PHONE_HUMAN, EMAIL)
 *
 * If the site changes any of them, change them here too — this file is the one
 * place the portal copies them. A tenant with no `marketingSiteUrl` (every
 * tenant except Aperio today) never reaches any of this.
 */

export type MarketingLink = {
  label: string;
  href: string;
};

/**
 * The site's published contact channels, as its own header and footer print
 * them. `tel:` uses the dialable form, the visible text the human one.
 */
export const MARKETING_PHONE = '+1-833-466-4520';
export const MARKETING_PHONE_HUMAN = '1-833-466-4520';
export const MARKETING_EMAIL = 'hello@aperiomobility.com';

/**
 * The legal entity, its registered office and the office hours, as the site's
 * own footer bottom row prints them (`LEGAL_ENTITY` / `LEGAL_ADDRESS` in
 * `aperio/site/build/util.js` and the `hours` chrome string in `build/shell.js`).
 * The brand, then both companies: Aperio Mobility is a trade name of Go Oil Canada Inc.
 * (owner, 2026-10-29; never "Aperio Mobility Inc."; `LEGAL_ENTITIES` in util.js).
 */
export const MARKETING_LEGAL_ENTITY = 'Aperio Mobility \u00b7 Go Oil Canada Inc.'
  + ' \u00b7 Go Oil USA Inc.';
export const MARKETING_LEGAL_ADDRESS = '500-167 Lombard Avenue, Winnipeg,'
  + ' Manitoba R3B 0V3, Canada';
export const MARKETING_HOURS = 'Office 8 a.m.\u20138 p.m. Central, every day';

/**
 * The site's own tagline, under the footer's logo - shell.js `tagline`, which
 * the owner set to 2017 for the footer (2026-10-30; the JSON-LD foundingDate stays
 * 2018, the first van, per 2026-10-21); keep the two the same.
 */
export const MARKETING_TAGLINE = 'Mobile maintenance for people, dealers and'
  + ' fleets. Founded in Winnipeg, 2017.';

/**
 * The one link the footer's first column carries under the tagline: the site's
 * About page (shell.js `foot()`, `t.about` → `/about`). MarketingFooter.tsx
 * prints it in that same place; its FR/ES labels are in FOOTER_COPY below.
 */
export const MARKETING_ABOUT = { label: 'About', path: '/about' };

/**
 * The readable quote page an AI assistant is asked to open (the API's answer
 * in plain words, 2026-10-25), as the site's footer prints it in its
 * "Using an AI assistant? ..." line (`aiQuoteLine` in
 * `aperio/site/build/util.js`: plain text in a translate="no" span, not a
 * link). The sentence around it is `footer.aiQuote` in
 * src/lang/{en,fr,es}/portal.json.
 */
export const MARKETING_QUOTE_API_LABEL = 'aperiomobility.com/quote';

/** A link label in the site's three languages (shell.js `D` tables). */
type Labels = { en: string; fr: string; es: string };

/**
 * Desktop header links, in the site's order. "For business" is a hover
 * dropdown on the site; a dropdown inside the portal's header would be a new
 * interaction to maintain for no gain, so it is the plain link to /fleet that
 * the dropdown's first and main entry points at. Labels: shell.js `D.*`
 * (how, pricing, business, cities). The site's "For business" is that same
 * plain link now (round 2, 2026-10-25). No "Investors" anywhere (owner,
 * 2026-10-25: no investor content in a customer's menu or footer).
 */
const HEADER_PATHS: { label: Labels; path: string }[] = [
  {
    label: { en: 'How it works', fr: 'Comment ça marche', es: 'Cómo funciona' },
    path: '/how',
  },
  { label: { en: 'Pricing', fr: 'Tarifs', es: 'Precios' }, path: '/pricing' },
  {
    label: {
      en: 'For business', fr: 'Pour les entreprises', es: 'Para empresas',
    },
    path: '/fleet',
  },
  {
    label: { en: 'Locations', fr: 'Villes', es: 'Ciudades' },
    path: '/locations',
  },
];

/**
 * The site's mobile panel, in its order: "For business" is the one /fleet
 * link there too (round 2, 2026-10-25; the DSP, dealer and repair-shop pages
 * are in the footer's Business column).
 */
const MOBILE_PATHS: { label: Labels; path: string }[] = [
  HEADER_PATHS[0],
  HEADER_PATHS[1],
  HEADER_PATHS[3],
  {
    label: { en: 'Vehicles', fr: 'Véhicules', es: 'Vehículos' },
    path: '/vehicles',
  },
  HEADER_PATHS[2],
  // The site's panel ends its links with Contact (shell.js nav(); QA
  // 2026-09-25 #47/#48). No Investors here either (owner, 2026-10-25).
  {
    label: { en: 'Contact', fr: 'Contact', es: 'Contacto' },
    path: '/contact',
  },
];

/**
 * The header's own words in the site's three languages: shell.js `D.login`,
 * `book`, `menu`, `closeMenu`, `lang`, `mainNav`, `mobileNav`, and the
 * signed-in account button's `account`, `logout`, `signedIn`, `acctMenu`
 * (shell.js `D`, drawn by site.js once the portal's hint cookie says so).
 */
const HEADER_CHROME: Record<'en' | 'fr' | 'es', {
  login: string;
  account: string;
  logout: string;
  signedIn: string;
  acctMenu: string;
  book: string;
  menu: string;
  closeMenu: string;
  lang: string;
  mainNav: string;
  mobileNav: string;
}> = {
  en: {
    login: 'Log in',
    account: 'My account',
    logout: 'Log out',
    signedIn: 'Signed in as {name}',
    acctMenu: 'Account menu',
    book: 'Book service',
    menu: 'Menu',
    closeMenu: 'Close menu',
    lang: 'Language',
    mainNav: 'Main',
    mobileNav: 'Site menu',
  },
  fr: {
    login: 'Connexion',
    account: 'Mon compte',
    logout: 'Se déconnecter',
    signedIn: 'Connecté en tant que {name}',
    acctMenu: 'Menu du compte',
    book: 'Réserver',
    menu: 'Menu',
    closeMenu: 'Fermer le menu',
    lang: 'Langue',
    mainNav: 'Principale',
    mobileNav: 'Menu du site',
  },
  es: {
    login: 'Iniciar sesión',
    account: 'Mi cuenta',
    logout: 'Cerrar sesión',
    signedIn: 'Sesión iniciada como {name}',
    acctMenu: 'Menú de la cuenta',
    book: 'Agendar servicio',
    menu: 'Menú',
    closeMenu: 'Cerrar menú',
    lang: 'Idioma',
    mainNav: 'Principal',
    mobileNav: 'Menú del sitio',
  },
};

/**
 * The footer's copy in the site's three languages, as its SSR `foot()` prints
 * it on `/`, `/fr` and `/es` (build the site and read the `<footer>` of
 * `dist/index.html`, `dist/fr/index.html`, `dist/es/index.html`). Labels come
 * from shell.js's D tables, `FOOT_SERVICES`, and — for Guides, App, Careers,
 * Press and the plan disclosure — `src/data/lang-{fr,es}-growth.js`.
 *
 * `page` is the site path without its language prefix; `''` is the home page
 * (the site's `L('', lang)`), so `hash: 'included'` is `/#included`,
 * `/fr#included`, `/es#included`. The column flagged `contact` ends with the
 * phone and the e-mail, exactly as the site's Company column does.
 */
export type MarketingLang = 'en' | 'fr' | 'es';

/** `href` is an absolute link (the free fleet dashboard sign-up); else page + hash. */
// The site's U.FLEET_SIGNUP (aperio/site/build/util.js).
export const FLEET_SIGNUP_URL = 'https://book.aperiomobility.com/fleet/signup';

type FooterItem = { label: string; page: string; hash?: string; href?: string };

type FooterGroup = {
  heading: string;
  items: FooterItem[];
  contact?: boolean;
};

type FooterCopy = {
  tagline: string;
  about: string;
  hours: string;
  phoneHuman: string;
  langGroup: string;
  disclosure: string;
  /** CCPA/CPRA "Your privacy choices" (see MARKETING_PRIVACY_CHOICES). */
  privacyChoices: string;
  groups: FooterGroup[];
};

const FOOTER_COPY: Record<MarketingLang, FooterCopy> = {
  en: {
    tagline: MARKETING_TAGLINE,
    about: MARKETING_ABOUT.label,
    hours: MARKETING_HOURS,
    phoneHuman: MARKETING_PHONE_HUMAN,
    langGroup: 'Language',
    privacyChoices: 'Your privacy choices',
    disclosure: 'Aperio Plan benefits are supported'
      + ' through our provider, Franc Financial Inc., a specialty risk and'
      + ' financial firm. As with all coverage, insurance and service plans,'
      + ' limitations and exclusions apply. Please contact customer support for'
      + ' full terms and details.',
    groups: [
      {
        heading: 'Product',
        items: [
          { label: 'How it works', page: 'how' },
          { label: 'Pricing', page: 'pricing' },
          { label: 'Ways to pay', page: 'payments' },
          { label: "What's included", page: '', hash: 'included' },
          { label: 'Services', page: 'services' },
          { label: 'Locations', page: 'locations' },
          { label: 'Vehicles we service', page: 'vehicles' },
          { label: 'Guides', page: 'guides' },
          { label: 'App', page: 'app' },
          { label: 'Referrals', page: 'referrals' },
        ],
      },
      {
        heading: 'Business',
        items: [
          { label: 'Fleets', page: 'fleet' },
          { label: 'Free fleet dashboard', page: '', href: FLEET_SIGNUP_URL },
          { label: 'Delivery partners', page: 'dsp' },
          { label: 'Dealers', page: 'dealers' },
          { label: 'Repair shops', page: 'repair-shops' },
          { label: 'Partner with us', page: 'partners' },
        ],
      },
      {
        heading: 'Company',
        contact: true,
        items: [
          { label: 'Contact', page: 'contact' },
          { label: 'Careers', page: 'careers' },
          { label: 'Press', page: 'press' },
          { label: 'Accessibility', page: 'accessibility' },
          { label: 'Privacy', page: 'privacy' },
          { label: 'Terms', page: 'terms' },
          { label: 'Facts and figures', page: 'facts' },
        ],
      },
    ],
  },
  fr: {
    tagline: 'Entretien mobile pour les particuliers, les concessionnaires et'
      + ' les flottes. Fondée à Winnipeg en 2017.',
    about: 'À propos',
    // U+00A0 before the colon, as the site prints it (French typography).
    hours: 'Bureau : 8 h à 20 h, heure du Centre, tous les jours',
    // util.js PHONE_HUMAN_FR: the Québec spaced form.
    phoneHuman: '1 833 466-4520',
    langGroup: 'Langue',
    privacyChoices: 'Vos choix en matière de confidentialité',
    disclosure: 'Les avantages du plan Aperio sont'
      + ' soutenus par notre fournisseur, Franc Financial Inc., une société'
      + ' spécialisée en gestion des risques et en services financiers. Comme'
      + ' pour toute protection, assurance ou tout plan de service, des'
      + " limitations et des exclusions s'appliquent. Communiquez avec le"
      + ' service à la clientèle pour les conditions complètes et les détails.',
    groups: [
      {
        heading: 'Produit',
        items: [
          { label: 'Comment ça marche', page: 'how' },
          { label: 'Tarifs', page: 'pricing' },
          { label: 'Modes de paiement', page: 'payments' },
          { label: 'Ce qui est inclus', page: '', hash: 'included' },
          { label: 'Services', page: 'services' },
          { label: 'Villes', page: 'locations' },
          { label: 'Véhicules entretenus', page: 'vehicles' },
          { label: 'Guides', page: 'guides' },
          { label: 'Application', page: 'app' },
          { label: 'Parrainage', page: 'referrals' },
        ],
      },
      {
        heading: 'Entreprises',
        items: [
          { label: 'Flottes', page: 'fleet' },
          { label: 'Tableau de bord de flotte gratuit', page: '', href: FLEET_SIGNUP_URL },
          { label: 'Partenaires de livraison', page: 'dsp' },
          { label: 'Concessionnaires', page: 'dealers' },
          { label: 'Garages partenaires', page: 'repair-shops' },
          { label: 'Devenir partenaire', page: 'partners' },
        ],
      },
      {
        heading: 'Entreprise',
        contact: true,
        items: [
          { label: 'Contact', page: 'contact' },
          { label: 'Carrières', page: 'careers' },
          { label: 'Presse', page: 'press' },
          { label: 'Accessibilité', page: 'accessibility' },
          { label: 'Confidentialité', page: 'privacy' },
          { label: 'Conditions', page: 'terms' },
          { label: 'Faits et chiffres', page: 'facts' },
        ],
      },
    ],
  },
  es: {
    tagline: 'Mantenimiento móvil para personas, concesionarios y flotillas.'
      + ' Fundada en Winnipeg en 2017.',
    about: 'Acerca de',
    hours: 'Oficina: 8 a. m. a 8 p. m., hora del Centro, todos los días',
    phoneHuman: MARKETING_PHONE_HUMAN,
    langGroup: 'Idioma',
    privacyChoices: 'Tus opciones de privacidad',
    disclosure: 'Los beneficios del Plan Aperio'
      + ' están respaldados por nuestro proveedor, Franc Financial Inc., una'
      + ' empresa especializada en riesgos y servicios financieros. Como en'
      + ' toda cobertura, seguro o plan de servicio, se aplican limitaciones y'
      + ' exclusiones. Comunícate con atención al cliente para conocer los'
      + ' términos completos y los detalles.',
    groups: [
      {
        heading: 'Producto',
        items: [
          { label: 'Cómo funciona', page: 'how' },
          { label: 'Precios', page: 'pricing' },
          { label: 'Formas de pago', page: 'payments' },
          { label: 'Qué incluye', page: '', hash: 'included' },
          { label: 'Servicios', page: 'services' },
          { label: 'Ciudades', page: 'locations' },
          { label: 'Vehículos que atendemos', page: 'vehicles' },
          { label: 'Guías', page: 'guides' },
          { label: 'App', page: 'app' },
          { label: 'Referidos', page: 'referrals' },
        ],
      },
      {
        heading: 'Empresas',
        items: [
          { label: 'Flotillas', page: 'fleet' },
          { label: 'Panel de flotilla gratis', page: '', href: FLEET_SIGNUP_URL },
          { label: 'Socios de entrega', page: 'dsp' },
          { label: 'Concesionarios', page: 'dealers' },
          { label: 'Talleres', page: 'repair-shops' },
          { label: 'Asóciate con nosotros', page: 'partners' },
        ],
      },
      {
        heading: 'Nosotros',
        contact: true,
        items: [
          { label: 'Contacto', page: 'contact' },
          { label: 'Empleo', page: 'careers' },
          { label: 'Prensa', page: 'press' },
          { label: 'Accesibilidad', page: 'accessibility' },
          { label: 'Privacidad', page: 'privacy' },
          { label: 'Términos', page: 'terms' },
          { label: 'Datos y cifras', page: 'facts' },
        ],
      },
    ],
  },
};

/**
 * The three languages the site serves. English is the site root; the others
 * are path-prefixed. The portal speaks the same three (src/lang/{en,fr,es},
 * Aperio preset only), so its language pill switches the portal in place
 * (portalLanguageHref / i18n setPortalLanguage) instead of leaving the flow
 * for the site's home page (QA 2026-09-25 #26).
 */
export const MARKETING_LANGUAGES: {
  code: string;
  label: string;
  path: string;
  hrefLang: string;
  name: string;
}[] = [
  {
    code: 'en', label: 'EN', path: '', hrefLang: 'en', name: 'English',
  },
  {
    code: 'fr', label: 'FR', path: '/fr', hrefLang: 'fr-CA', name: 'Français',
  },
  {
    code: 'es', label: 'ES', path: '/es', hrefLang: 'es', name: 'Español',
  },
];

/** Trailing slashes would double up against the paths above. */
export function marketingBase(url?: string | null): string | null {
  const base = (url || '').trim().replace(/\/+$/, '');
  return base || null;
}

function toLinks(
  paths: { label: Labels; path: string }[],
  url?: string | null,
  lang?: string | null,
): MarketingLink[] {
  const base = marketingBase(url);
  if (!base) return [];
  // Into the tree of the language the visitor was reading (QA 2026-09-25
  // #26: the header sent a French visitor to the English pages), as the
  // footer already does.
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const code = footerLang(lang);
  return paths.map(({ label, path }) => ({
    label: label[code],
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    href: `${base}${sitePath(code, path.replace(/^\//, ''))}`,
  }));
}

export function marketingHeaderLinks(
  url?: string | null,
  lang?: string | null,
): MarketingLink[] {
  return toLinks(HEADER_PATHS, url, lang);
}

export function marketingMobileLinks(
  url?: string | null,
  lang?: string | null,
): MarketingLink[] {
  return toLinks(MOBILE_PATHS, url, lang);
}

function footerLang(lang?: string | null): MarketingLang {
  return lang === 'fr' || lang === 'es' ? lang : 'en';
}

/** The header's own words (Log in, Book service, Menu ...) in `lang`. */
export function marketingHeaderChrome(lang?: string | null) {
  return HEADER_CHROME[footerLang(lang)];
}

/**
 * The portal URL of the page on screen, in another language: the same path,
 * the same query, `lang` set. It is the href of the language pill's links, so
 * a new tab or a copied link lands in that language; a plain click switches
 * in place instead (AperioSiteHeader / MarketingFooter).
 */
export function portalLanguageHref(
  pathname: string,
  search: string,
  lang: string,
): string {
  const params = new URLSearchParams(search);
  params.set('lang', lang);
  return `${pathname}?${params.toString()}`;
}

/** The site's home page in `lang` (`/`, `/fr`, `/es`). */
export function marketingHomeHref(url: string, lang?: string | null): string {
  const base = marketingBase(url) || '';
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return `${base}${sitePath(footerLang(lang), '')}`.replace(/\/$/, '')
    || base;
}

/** The site's `L(page, lang)`: '' is that language's home page. */
function sitePath(lang: MarketingLang, page: string): string {
  const prefix = lang === 'en' ? '' : `/${lang}`;
  if (!page) return prefix || '/';
  return `${prefix}/${page}`;
}

/**
 * The footer's non-link copy (tagline, About, hours, phone, the language
 * group's label, the plan disclosure) in `lang`; unknown languages get EN.
 */
export function marketingFooterCopy(lang?: string | null) {
  const {
    tagline, about, hours, phoneHuman, langGroup, disclosure, privacyChoices,
  } = FOOTER_COPY[footerLang(lang)];
  return {
    tagline, about, hours, phoneHuman, langGroup, disclosure, privacyChoices,
  };
}

/**
 * "Your privacy choices" in the footer's bottom row: the CCPA/CPRA opt-out of
 * "sharing" for advertising (aperio/site/docs/TRACKING.md). The site prints it
 * after "Privacy settings" ONLY while one of its advertising ids is set
 * (build/shell.js privacyChoices()), so this switch must follow the same ids:
 * true since the activation (2026-09-25) set Google Ads, Meta, Microsoft and
 * Nextdoor (LinkedIn followed on 2026-09-26) on the site and in
 * util/aperioTrackingConfig.ts; false again only if every advertising id is
 * emptied on both.
 *
 * The portal cannot open the site's consent settings, so the link goes to
 * the site's /privacy#choices, which opens them with advertising already off.
 */
export const MARKETING_PRIVACY_CHOICES = true;

/** /privacy#choices in the visitor's language tree, or null while off. */
export function marketingPrivacyChoicesHref(
  url?: string | null,
  lang?: string | null,
  enabled: boolean = MARKETING_PRIVACY_CHOICES,
): string | null {
  const base = marketingBase(url);
  if (!enabled || !base) return null;
  return `${base}${sitePath(footerLang(lang), 'privacy')}#choices`;
}

/**
 * The footer's link columns, with absolute hrefs into the site's tree for
 * `lang` (default EN, the site root).
 */
export function marketingFooterGroups(
  url?: string | null,
  lang?: string | null,
) {
  const base = marketingBase(url);
  if (!base) return [];
  const l = footerLang(lang);
  return FOOTER_COPY[l].groups.map((group) => ({
    heading: group.heading,
    contact: !!group.contact,
    items: group.items.map(({
      label, page, hash, href,
    }) => ({
      label,
      href: href || `${base}${sitePath(l, page)}${hash ? `#${hash}` : ''}`,
    })),
  }));
}

/** The first column's About link in `lang`. */
export function marketingAboutHref(url: string, lang?: string | null): string {
  const base = marketingBase(url) || '';
  return `${base}${sitePath(footerLang(lang), MARKETING_ABOUT.path.slice(1))}`;
}

/**
 * The language the visitor was reading the marketing site in, stashed by
 * usePrefilledAddress on the way in. Defaults to the site's own default,
 * English, when the visitor did not arrive through the hand-off.
 */
export function handoffLanguage(): string {
  // The portal runs its own translations (Aperio preset): the language on
  // screen is the one to follow, whatever the hand-off said.
  if (isPortalI18nActive()) return portalLanguage();
  try {
    const lang = (window.sessionStorage.getItem(HANDOFF_LANG_KEY) || '')
      .trim()
      .toLowerCase()
      .slice(0, 2);
    return MARKETING_LANGUAGES.some((l) => l.code === lang) ? lang : 'en';
  } catch (e) {
    // sessionStorage unavailable (private mode): assume the site's default.
    return 'en';
  }
}

/**
 * Where an address we cannot serve is sent: the marketing site's home page,
 * in the visitor's language, with the address in the hero box (the site's
 * `form.addr#address`; its addr.js reads `?address=` and runs its own
 * coverage check on it, which is what offers the waitlist). Null for a
 * tenant with no marketing site.
 */
export function marketingWaitlistUrl(
  url: string | null | undefined,
  address: string,
  lang: string = handoffLanguage(),
): string | null {
  const base = marketingBase(url);
  if (!base) return null;
  const language = MARKETING_LANGUAGES.find((l) => l.code === lang);
  const prefix = language ? language.path : '';
  const query = address.trim()
    ? `?address=${encodeURIComponent(address.trim())}`
    : '';
  return `${base}${prefix}/${query}#address`;
}
